/**
 * @file NotFoundTab.tsx
 * @description Вкладка «Не найдено» раздела «Сайт → SEO» (phase19.16,
 * партия «SEO-техника», v0.19.65; SB148, SB131, SB55; макет
 * uiux-seo-tech.md §3.1, тексты состояний §4): адреса сайта, по которым
 * посетители и поисковики получили «Страница не найдена», — поиск, период,
 * «Обработанные и скрытые», порядок, список (таблица с 1024, карточки ниже),
 * «Показать ещё» по 50 и строка «прочие».
 *
 * Действия (только с правом настроек, SB55): «Переадресовать» открывает
 * окно «Добавить переадресацию» с заполненным «откуда» и фокусом на «Куда»;
 * «Изменить» — правку недействующего правила; «⋯ → Скрыть» — сразу, тост
 * «Адрес … скрыт. [Вернуть]». После сохранения переадресации журнал и
 * счётчик вкладки «Переадресации» перечитываются. Без права — список,
 * «Проверить» и «Открыть на сайте ↗» с пояснением, кто меняет.
 */

import type { NotFoundJournalRowDto } from "@chaiberi/shared";
import { SearchX } from "lucide-react";
import { useState } from "react";

import { Button } from "../../ui/button";
import { ListError } from "../../ui/ListStates";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { useNotify } from "../../ui/NotifyProvider";
import { ResponsiveList } from "../../ui/ResponsiveList";
import type { ListEmptySpec } from "../../ui/responsive-list-types";
import { RedirectDialog } from "../redirects/RedirectDialog";
import { RedirectProbeDialog } from "../redirects/RedirectProbeDialog";
import { NotFoundFilters } from "./NotFoundFilters";
import { notFoundColumns, notFoundRowActions, notFoundRowKey } from "./not-found-columns";
import type { NotFoundListRow, NotFoundRowHandlers } from "./NotFoundRow";
import { useShopContextKey, useSiteAccess } from "../../../hooks/site-access/useSiteAccess";
import type { NotFoundApi } from "../../../hooks/site-seo/useNotFound";
import { useShopSiteUrl } from "../../../hooks/useShopSiteUrl";
import { siteRedirects, type SiteRedirectItem } from "../../../lib/api-site-redirects";
import { activeSiteClosure } from "../../../lib/site-access/site-access-view";
import { BOTS_HINT, NOT_FOUND_EMPTY_TEXT, NOT_FOUND_READONLY_TEXT, NOT_FOUND_UNAVAILABLE_TEXT, OTHER_HINT, notFoundEmptyKind, sinceText } from "../../../lib/site-seo/not-found-view";
import { seoErrorText } from "../../../lib/site-seo/seo-errors";

interface Props {
  api: NotFoundApi;
  canEdit: boolean;
  /** Переадресация добавлена или изменена — перечитать счётчик вкладки «Переадресации». */
  onRedirectsChanged: () => void;
}

export function NotFoundTab({ api, canEdit, onRedirectsChanged }: Props) {
  const notify = useNotify();
  const siteUrl = useShopSiteUrl();
  const access = useSiteAccess(useShopContextKey(), "read");
  const closed = activeSiteClosure(access.state, access.serverNow()) !== null;
  const [creatingFrom, setCreatingFrom] = useState<string | null>(null);
  const [editing, setEditing] = useState<SiteRedirectItem | null>(null);
  const [probe, setProbe] = useState<string | null>(null);
  const journal = api.journal;

  async function openEdit(row: NotFoundJournalRowDto) {
    try {
      // Правило ищем по адресу среди недействующих: поиск по подстроке — берём только точное совпадение «откуда».
      const page = await siteRedirects.list({ q: row.display, status: "inactive", limit: 100 });
      const item = page.items.find((i) => i.fromPath === row.path);
      if (item) setEditing(item);
      else {
        notify.error("Этой переадресации больше нет — журнал обновлён.");
        void api.reload();
      }
    } catch (err) {
      notify.error(seoErrorText(err, "Не удалось открыть переадресацию."));
    }
  }

  async function unhide(row: NotFoundJournalRowDto) {
    try {
      await api.unhide(row.path);
    } catch (err) {
      notify.error(seoErrorText(err, "Не удалось вернуть адрес — попробуйте ещё раз."));
    }
  }

  async function hide(row: NotFoundJournalRowDto) {
    try {
      await api.hide(row.path);
      notify.success(`Адрес ${row.display} скрыт.`, { action: { label: "Вернуть", onClick: () => void unhide(row) }, duration: 8000 });
    } catch (err) {
      notify.error(seoErrorText(err, "Не удалось скрыть адрес — попробуйте ещё раз."));
    }
  }

  const handlers: NotFoundRowHandlers = {
    canEdit,
    today: journal?.period.to ?? "",
    sort: api.sort,
    onSort: api.setSort,
    onRedirect: (row) => setCreatingFrom(row.display),
    onEdit: (row) => void openEdit(row),
    onProbe: setProbe,
    onHide: (row) => void hide(row),
    onUnhide: (row) => void unhide(row),
    siteUrl,
  };

  const other = journal && !api.hasMore && journal.other.visitors + journal.other.bots > 0 ? journal.other : null;
  const rows: NotFoundListRow[] = [
    ...api.rows.map((row): NotFoundListRow => ({ kind: "path", row })),
    ...(other && journal ? [{ kind: "other" as const, visitors: other.visitors, bots: other.bots, lastDay: journal.period.to }] : []),
  ];
  const emptyKind = journal ? notFoundEmptyKind(journal, api.query, api.all) : "none";
  const handled = journal?.handledCount ?? 0;
  const showHandled =
    !api.all && handled > 0 ? (
      <Button variant="outline" onClick={() => api.setAll(true)}>
        Показать обработанные и скрытые ({handled})
      </Button>
    ) : undefined;
  const empty: ListEmptySpec = { icon: SearchX, ...NOT_FOUND_EMPTY_TEXT[emptyKind === "search" ? "none" : emptyKind], action: showHandled };
  const since = journal ? sinceText(journal) : null;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <p className="type-body text-on-surface-variant">
        Адреса вашего сайта, по которым посетители и поисковики получили «Страница не найдена». Переадресуйте их на подходящую страницу или скройте мусор.
      </p>
      {canEdit ? null : <p className="type-caption text-on-surface-variant">{NOT_FOUND_READONLY_TEXT}</p>}
      {closed ? <NoticeStrip>Сайт закрыт для покупателей — пока он закрыт, журнал не пополняется.</NoticeStrip> : null}
      <NotFoundFilters api={api} />
      {since ? <p className="type-caption text-on-surface-variant">{since}</p> : null}
      {api.unavailable ? (
        <ListError message={NOT_FOUND_UNAVAILABLE_TEXT} onRetry={() => void api.reload()} />
      ) : (
        <ResponsiveList<NotFoundListRow>
          aria-label="Журнал «Не найдено»"
          rows={rows}
          columns={notFoundColumns(handlers)}
          rowKey={notFoundRowKey}
          rowActions={(item) => notFoundRowActions(item, handlers)}
          rowActionsTitle={(item) => (item.kind === "path" ? `Действия: ${item.row.display}` : "Действия")}
          rowActionsSize="default"
          compactWrap
          compactRowSize="tall"
          loading={api.loading}
          error={api.error}
          onRetry={() => void api.reload()}
          filtered={emptyKind === "search"}
          empty={empty}
          filteredEmpty={{ icon: SearchX, ...NOT_FOUND_EMPTY_TEXT.search, onReset: () => api.setQuery(""), resetLabel: "Сбросить" }}
          skeletonRows={4}
        />
      )}
      {api.hasMore ? (
        <Button variant="outline" className="self-center max-sm:w-full" loading={api.loadingMore} onClick={() => void api.loadMore()}>
          Показать ещё
        </Button>
      ) : null}
      {rows.length > 0 ? <p className="type-caption text-on-surface-variant">{BOTS_HINT}</p> : null}
      {other ? <p className="type-caption text-on-surface-variant">Прочие адреса: {OTHER_HINT}</p> : null}
      <RedirectDialog
        open={creatingFrom !== null || editing !== null}
        editing={editing}
        initialFrom={creatingFrom}
        onClose={() => {
          setCreatingFrom(null);
          setEditing(null);
        }}
        onSaved={() => {
          void api.reload();
          onRedirectsChanged();
        }}
        onEditExisting={(item) => {
          setCreatingFrom(null);
          setEditing(item);
        }}
      />
      <RedirectProbeDialog path={probe} onClose={() => setProbe(null)} />
    </div>
  );
}
